import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import api from '../api';

/**
 * Admin > Referrals — searchable table of every referrer→referred relationship.
 * Rows show commission earned per referral so admins can spot big earners and
 * detect suspicious clustering (e.g., one referrer pushing lots of low-activity
 * accounts). Read-only; the "Refer friends" flow is user-driven.
 */
export default function AdminReferrals() {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const PAGE = 50;

  const load = useCallback(async (nextOffset = 0, nextQ = q) => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/referrals', {
        params: { q: nextQ, limit: PAGE, offset: nextOffset },
      });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setOffset(nextOffset);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(0, ''); /* eslint-disable-next-line */ }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(0, q);
  };

  const totalCommission = rows.reduce((s, r) => s + (r.commission_earned || 0), 0);
  const totalTrades = rows.reduce((s, r) => s + (r.trades_count || 0), 0);

  return (
    <AdminLayout active="referrals">
      <div className="admin-page" data-testid="admin-referrals-page">
        <div className="admin-page-head">
          <div>
            <h1 className="admin-page-title">Referrals</h1>
            <p className="admin-page-sub">
              Every referrer → referred relationship in the system, sorted newest first.
              Search matches either side by handle, email, or display name.
            </p>
          </div>
        </div>

        <div className="admin-metrics-row">
          <MetricCard label="Total referrals" value={total.toLocaleString()} testid="ar-total" />
          <MetricCard label="Commission on page" value={`${totalCommission.toFixed(2)} pts`} testid="ar-commission" />
          <MetricCard label="Trades on page" value={totalTrades.toLocaleString()} testid="ar-trades" />
        </div>

        <form className="admin-search-row" onSubmit={onSearch}>
          <input
            type="text"
            className="admin-search-input"
            placeholder="Search handle, email, or display name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-referrals-search"
          />
          <button type="submit" className="admin-btn primary" data-testid="admin-referrals-search-btn">Search</button>
          {q && (
            <button
              type="button"
              className="admin-btn"
              onClick={() => { setQ(''); load(0, ''); }}
              data-testid="admin-referrals-clear-btn"
            >
              Clear
            </button>
          )}
        </form>

        <div className="admin-table-wrap">
          <table className="admin-table" data-testid="admin-referrals-table">
            <thead>
              <tr>
                <th>Referrer</th>
                <th>Referred</th>
                <th>Joined</th>
                <th className="num">Trades</th>
                <th className="num">Fees paid</th>
                <th className="num">Commission</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan={6} className="admin-table-empty">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="admin-table-empty">No referrals match.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} data-testid={`admin-referral-row-${r.id}`}>
                  <td>
                    <UserCell u={r.referrer} />
                  </td>
                  <td>
                    <UserCell u={r.referred} />
                  </td>
                  <td className="admin-cell-muted">{formatDate(r.joined_at)}</td>
                  <td className="num">{r.trades_count}</td>
                  <td className="num">{r.fees_paid.toFixed(2)}</td>
                  <td className="num" style={{ color: r.commission_earned > 0 ? '#B4FF00' : undefined }}>
                    +{r.commission_earned.toFixed(4)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="admin-pagination">
          <button
            className="admin-btn"
            disabled={offset === 0}
            onClick={() => load(Math.max(0, offset - PAGE), q)}
            data-testid="admin-referrals-prev"
          >
            ← Previous
          </button>
          <span className="admin-pagination-info">
            Showing {rows.length === 0 ? 0 : offset + 1}–{offset + rows.length} of {total.toLocaleString()}
          </span>
          <button
            className="admin-btn"
            disabled={offset + PAGE >= total}
            onClick={() => load(offset + PAGE, q)}
            data-testid="admin-referrals-next"
          >
            Next →
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}

function UserCell({ u }) {
  if (!u || !u.handle) return <span className="admin-cell-muted">{u?.email || '—'}</span>;
  return (
    <a href={`/creator/${u.handle}`} target="_blank" rel="noreferrer" className="admin-user-link">
      <div className="admin-user-name">{u.display_name}</div>
      <div className="admin-user-handle">@{u.handle}</div>
    </a>
  );
}

function MetricCard({ label, value, testid }) {
  return (
    <div className="admin-metric-card" data-testid={testid}>
      <div className="admin-metric-label">{label}</div>
      <div className="admin-metric-value">{value}</div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
